import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import type { WhatsAppForwarding as WhatsAppForwardingType } from "@/apis/gql/types";
import { TextInput } from "@/components/shared/form/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { CopyToClipboardCard } from "@/components/ui/copy-to-clipboard-card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import { Skeleton } from "@/components/ui/skeleton";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";
import { Check, Edit2, Plus, QrCode, Trash2, X } from "lucide-react";
import { useState } from "react";
import {
  type Value,
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from "react-phone-number-input/input";

const whatsappFormSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, t("forwarding.whatsapp.phone_number_required"))
    .refine(
      (value) => {
        // isValidPhoneNumber expects undefined instead of empty string
        if (!value) return false;
        return isValidPhoneNumber(value);
      },
      {
        message: t("forwarding.whatsapp.phone_number_invalid"),
      },
    ),
  name: z.string().trim().min(1, t("forwarding.whatsapp.name_required")),
});

type WhatsAppFormValues = z.infer<typeof whatsappFormSchema>;

type Props = {
  whatsappForwardings: WhatsAppForwardingType[];
  loading: boolean;
  onDeleteWhatsAppForwarding: (whatsappForwardingId: string) => Promise<void>;
  onUpdateWhatsAppForwarding: ({
    whatsappForwardingId,
    phoneNumber,
    name,
    onboardingState,
  }: {
    whatsappForwardingId: string;
    phoneNumber?: string;
    name?: string;
    onboardingState?: string;
  }) => Promise<void>;
  onCreateWhatsAppForwarding: ({
    phoneNumber,
    name,
  }: {
    phoneNumber: string;
    name: string;
  }) => Promise<void>;
};

const PhoneNumberRow = ({
  number,
  isEditing,
  onEdit,
  onDelete,
  onSave,
  onCancel,
}: {
  number: WhatsAppForwardingType;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (phone: string, name: string) => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation();

  const editForm = useForm<WhatsAppFormValues>({
    resolver: zodResolver(whatsappFormSchema),
    mode: "onSubmit",
    defaultValues: {
      phoneNumber: number.phoneNumber,
      name: number.name,
    },
  });

  const handleEditSave = async (data: WhatsAppFormValues) => {
    onSave(data.phoneNumber, data.name);
  };

  if (isEditing) {
    return (
      <div className="flex flex-col lg:grid lg:grid-cols-[1fr_1fr_auto] gap-3 px-3 py-3">
        <Form {...editForm}>
          <form
            onSubmit={editForm.handleSubmit(handleEditSave)}
            className="contents"
          >
            <FormField
              control={editForm.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PhoneInput
                      placeholder={formatPhoneNumberIntl(
                        t(
                          "forwarding.whatsapp.phone_number_placeholder",
                        ) as string,
                      )}
                      value={field.value as Value}
                      onChange={(value) => field.onChange(value || "")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <TextInput
              name="name"
              placeholder={t("forwarding.whatsapp.name_placeholder") as string}
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                size="sm"
                className="gap-1 flex-1 lg:flex-initial translate-y-0.5"
              >
                <Check className="h-4 w-4 lg:mr-1" />
                <span className="lg:inline">{t("save")}</span>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={onCancel}
                className="gap-1 flex-1 lg:flex-initial translate-y-0.5"
              >
                <X className="h-4 w-4 lg:mr-1" />
                <span className="lg:inline">{t("cancel")}</span>
              </Button>
            </div>
          </form>
        </Form>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 px-3 py-3 hover:bg-slate-50 transition-colors lg:grid lg:grid-cols-[1fr_1fr_auto] lg:items-center">
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <div className="font-mono text-sm truncate">
          {formatPhoneNumberIntl(number.phoneNumber)}
        </div>
        <div className="text-sm text-gray-700 truncate">{number.name}</div>
      </div>
      <div className="flex gap-1 shrink-0 lg:col-start-3">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
  );
};

const PhoneNumberRowSkeleton = () => {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-3">
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
      <div className="flex gap-1 shrink-0">
        <Skeleton className="h-8 w-8" />
        <Skeleton className="h-8 w-8" />
      </div>
    </div>
  );
};

/**
 * How to create a branded QR Code:
 * 1. https://www.qrcode-monkey.com
 * 2. Input link
 * 3. Choose colors: #000000 and #FFFFFF
 * 4. Add svg logo and select "Remove background Behind Logo"
 * 5. Customize design: Body shape #4 + Eye Frame shape #13 + Eye Ball shape #14
 * 6. Export as PNG 700x700
 */
const WhatsAppQRCode = () => {
  return (
    <img
      src="/whatsapp-qr-code.png"
      alt={t("forwarding.whatsapp.scan_qr_code")}
      className="rounded-xl max-w-full h-[300px]"
    />
  );
};

export const WhatsAppForwarding = ({
  whatsappForwardings,
  loading,
  onCreateWhatsAppForwarding,
  onDeleteWhatsAppForwarding,
  onUpdateWhatsAppForwarding,
}: Props) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);

  const tabulaWhatsAppNumber = process.env
    .VITE_TABULA_WHATSAPP_PHONE_NUMBER as string;

  const form = useForm<WhatsAppFormValues>({
    resolver: zodResolver(whatsappFormSchema),
    mode: "onSubmit",
    defaultValues: {
      phoneNumber: "",
      name: "",
    },
  });

  const handleAdd = async (data: WhatsAppFormValues) => {
    try {
      await onCreateWhatsAppForwarding({
        phoneNumber: data.phoneNumber,
        name: data.name,
      });
      form.reset();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDelete = async (whatsappForwardingId: string) => {
    if (editingId === whatsappForwardingId) {
      setEditingId(null);
    }

    try {
      await onDeleteWhatsAppForwarding(whatsappForwardingId);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleEdit = (whatsappForwardingId: string) => {
    setEditingId(whatsappForwardingId);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (
    whatsappForwardingId: string,
    updatedPhone: string,
    updatedName: string,
  ) => {
    try {
      await onUpdateWhatsAppForwarding({
        whatsappForwardingId,
        phoneNumber: updatedPhone,
        name: updatedName,
      });
      setEditingId(null);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="space-y-2">
          <TypographyLarge>{t("forwarding.whatsapp.title")}</TypographyLarge>
          <TypographySmall className="text-muted-foreground">
            {t("forwarding.whatsapp.description")}
          </TypographySmall>
        </div>
      </CardHeader>
      <div className="flex flex-col gap-4 px-4 pb-4">
        <CopyToClipboardCard
          label={t("forwarding.whatsapp.phone_number")}
          value={tabulaWhatsAppNumber}
          displayValue={formatPhoneNumberIntl(tabulaWhatsAppNumber)}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsQrDialogOpen(true)}
          >
            <QrCode className="h-4 w-4 mr-2" />
            {t("forwarding.whatsapp.qr_code")}
          </Button>
        </CopyToClipboardCard>

        <div className="flex flex-col gap-4">
          <TypographyLarge>
            {t("forwarding.whatsapp.allowed_numbers")}
          </TypographyLarge>

          <div className="px-3">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleAdd)}
                className="flex flex-col lg:grid lg:grid-cols-[1fr_1fr_auto] gap-4"
              >
                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("forwarding.whatsapp.phone_number_label")}{" "}
                        <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <PhoneInput
                          placeholder={formatPhoneNumberIntl(
                            t(
                              "forwarding.whatsapp.phone_number_placeholder",
                            ) as string,
                          )}
                          value={field.value as Value}
                          onChange={(value) => field.onChange(value || "")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <TextInput
                  name="name"
                  label={t("forwarding.whatsapp.name_label") as string}
                  required
                  placeholder={
                    t("forwarding.whatsapp.name_placeholder") as string
                  }
                />
                <Button
                  type="submit"
                  size="sm"
                  className="lg:mt-8 lg:translate-y-0.5"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t("forwarding.whatsapp.add")}
                </Button>
              </form>
            </Form>
          </div>

          {loading ? (
            <div>
              {Array.from({ length: 2 }).map((_, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  key={index}
                  className={index > 0 ? "border-t" : ""}
                >
                  <PhoneNumberRowSkeleton />
                </div>
              ))}
            </div>
          ) : (
            whatsappForwardings.length > 0 && (
              <div>
                {whatsappForwardings.map((forwarding, index) => (
                  <div
                    key={forwarding.whatsappForwardingId}
                    className={index > 0 ? "border-t" : ""}
                  >
                    <PhoneNumberRow
                      number={forwarding}
                      key={
                        editingId === forwarding.whatsappForwardingId
                          ? "editing"
                          : "not-editing"
                      }
                      isEditing={editingId === forwarding.whatsappForwardingId}
                      onEdit={() => handleEdit(forwarding.whatsappForwardingId)}
                      onDelete={() =>
                        handleDelete(forwarding.whatsappForwardingId)
                      }
                      onSave={(phone, name) =>
                        handleSaveEdit(
                          forwarding.whatsappForwardingId,
                          phone,
                          name,
                        )
                      }
                      onCancel={handleCancelEdit}
                    />
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
        <DialogContent className="max-w-[384px]">
          <DialogHeader>
            <DialogTitle>{t("forwarding.whatsapp.scan_qr_code")}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            <WhatsAppQRCode />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
